import { Button } from '@/components/ui/button';
import { type CMSCallToAction } from '../schemas';
import { useResponsive } from '@/hooks/use-responsive';
import { cn } from '@/lib/utils';
import { useCTA } from '@/hooks/use-cta';
import { DynamicHeroIcon } from '@/components/dynamic-heroicon';
import { IconButton } from '@/components/ui/icon-button';
import { getButtonPositionStyle } from './utils';

interface CMSCTAButtonProps {
  ctaData: Omit<CMSCallToAction, 'id' | 'type'>;
  className?: string;
  buttonClassName?: string;
}
const CMSCTAButton = ({
  ctaData,
  className,
  buttonClassName,
}: CMSCTAButtonProps) => {
  const variant = ctaData.style.key;
  const url = ctaData.url;
  const target = ctaData.target.key;
  const buttonPosition = ctaData.button_position.key;
  const gtmCta = ctaData.gtmCta;
  const enableGTM = ctaData.enable_gtm;
  const { isTablet, isDesktop } = useResponsive();

  const buttonPositionStyle = getButtonPositionStyle(buttonPosition);

  const onClick = useCTA({
    target,
    drawerContent: ctaData.drawer_content.key,
    url,
    gtmCta,
    enableGTM,
  });

  if (variant === 'link') {
    return (
      <div
        className={cn(buttonPositionStyle, className)}
        data-component="CMSCTAButton"
      >
        {ctaData.icon_text ? (
          <IconButton onClick={onClick} variant="link" className="iconButton">
            {ctaData.title}{' '}
            <DynamicHeroIcon
              iconName={ctaData.icon_text}
              shape={ctaData.icon_style.key}
              asChild
            />
          </IconButton>
        ) : (
          // use regular a tag if it has no icon text
          <a
            onClick={onClick}
            className="text-black underline text-sm md:text-base lg:text-lg cursor-pointer font-nunito500"
            href={url}
            target={target}
          >
            {ctaData.title}
          </a>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(buttonPositionStyle, className)}
      data-component="CMSCTAButton"
    >
      <Button
        onClick={onClick}
        className={buttonClassName}
        size={isTablet || isDesktop ? 'medium' : 'small'}
        variant={variant}
      >
        {ctaData.title}

        {ctaData.icon_text && (
          <DynamicHeroIcon
            iconName={ctaData.icon_text}
            shape={ctaData.icon_style.key}
            asChild
          />
        )}
      </Button>
    </div>
  );
};

export { CMSCTAButton };
